import { Action, Selector, State, StateContext } from '@ngxs/store';
import { BaseStateInterface } from './basestate.state';
import { QuestionnaireCsrdDto } from 'app/@types/dto/questionnaire-csrd-dto.model';
import { QuestionCsrdInstanceDto } from 'app/@types/dto/question-csrd-instance-dto.model';
import { QuestionCsrdDto } from 'app/@types/dto/question-csrd-dto.model';
import { Injectable } from '@angular/core';
import { QuestionnaireCsrdHttpService } from '@services/datasources/questionnaireCsrd-http.service';
import { tap } from 'rxjs';
import { ResultsCsrdFetcherService } from '@services/results-csrd-fetcher/results-csrd-fetcher.service';

export class QuestionnaireCsrdModel implements BaseStateInterface {
    isInit: boolean = false;
    isLoading: boolean = false;
    errors: string[] = [];

    questionnaire?: QuestionnaireCsrdDto;
    questions?: QuestionCsrdDto[];
    questionsPerPage?: { [page: string]: { [section: string]: QuestionCsrdDto[] } };
    questionInstancePerQuestionCode?: Map<string, QuestionCsrdInstanceDto[]>;
}

// eslint-disable-next-line @typescript-eslint/no-namespace -- NGXS action grouping requires namespace for combined value+type semantics
export namespace QuestionnaireCsrdAction {
    export class Activate {
        public static type = '[questionnaireCsrd] Activate questionnaireCsrd';

        constructor(
            public organizationId: string,
            public year: number,
        ) {}
    }

    export class ChangeQuestionnaireTypeAction {
        public static type = "[questionnaireCsrd] Change questionnaire's type";

        constructor(
            public isComprehensive: boolean,
            public isDouble: boolean,
        ) {}
    }

    export class CreateUpdateQuestionInstance {
        public static type = '[questionnaireCsrd] Create or Update a questioninstance';
        constructor(
            public questionInstance: QuestionCsrdInstanceDto,
            public organizationId: string,
            public modifiedInstanceCallback: (instances: QuestionCsrdInstanceDto) => void,
        ) {}
    }

    export class DeleteQuestionInstance {
        public static type = '[questionnaireCsrd] Delete QuestionInstance';
        constructor(
            public questionInstance: QuestionCsrdInstanceDto,
            public organizationId: string,
        ) {}
    }
}

@State<QuestionnaireCsrdModel>({
    name: 'questionnaireCsrd',
    defaults: {
        questionnaire: undefined,
        questionInstancePerQuestionCode: undefined,
        isInit: false,
        isLoading: false,
        errors: [],
    },
})
@Injectable()
export class QuestionnaireCsrdState {
    constructor(
        private questionnaireCsrdService: QuestionnaireCsrdHttpService,
        private resultCsrdFetcherService: ResultsCsrdFetcherService,
    ) {}

    @Action(QuestionnaireCsrdAction.ChangeQuestionnaireTypeAction)
    changeQuestionnaireType(
        ctx: StateContext<QuestionnaireCsrdModel>,
        action: QuestionnaireCsrdAction.ChangeQuestionnaireTypeAction,
    ) {
        this.resultCsrdFetcherService.setInvalid();
        const currentQuestionnaire = ctx.getState().questionnaire;
        if (!currentQuestionnaire) {
            return;
        }
        const newQuest: QuestionnaireCsrdDto = {
            ...currentQuestionnaire,
            isComprehensive: action.isComprehensive,
            isDoubleMat: action.isDouble,
        };
        return this.questionnaireCsrdService.updateQuestionnaire(newQuest).pipe(
            tap(() => {
                ctx.patchState({ questionnaire: newQuest });
            }),
        );
    }

    @Action(QuestionnaireCsrdAction.Activate)
    activate(ctx: StateContext<QuestionnaireCsrdModel>, action: QuestionnaireCsrdAction.Activate) {
        ctx.patchState({ isLoading: true });

        // Return observable, so we can wait for it all to load with store.dispatch!
        return this.questionnaireCsrdService.getQuestionnaireCsrd(action.organizationId, action.year).pipe(
            tap((questionnaireWithInstances) => {
                this.resultCsrdFetcherService.setInvalid();
                ctx.patchState({
                    questionnaire: questionnaireWithInstances.questionnaire,
                    questionInstancePerQuestionCode: this.mapInstancesWithQuestionCode(
                        questionnaireWithInstances.questionInstances,
                    ),
                    isLoading: false,
                    isInit: true,
                });
            }),
        );
        // Then patch context
    }

    @Action(QuestionnaireCsrdAction.CreateUpdateQuestionInstance)
    createOrUpdate(
        ctx: StateContext<QuestionnaireCsrdModel>,
        action: QuestionnaireCsrdAction.CreateUpdateQuestionInstance,
    ) {
        return this.questionnaireCsrdService
            .createUpdateQuestionInstance(action.organizationId, action.questionInstance)
            .pipe(
                tap((updatedInstance) => {
                    const map = ctx.getState().questionInstancePerQuestionCode;
                    if (map === undefined) {
                        console.error('critital error');
                        return;
                    }
                    let instances = map.get(updatedInstance.questionCode) ?? [];
                    if (action.questionInstance.id === undefined) {
                        // createed, therefore can safely bruteforce add it
                        instances?.push(updatedInstance);
                    } else {
                        instances = instances.map((i) => (i.id === updatedInstance.id ? updatedInstance : i));
                    }
                    map.set(action.questionInstance.questionCode, instances);
                    this.resultCsrdFetcherService.setInvalid();
                    // Retrigger a patch state just in case
                    ctx.patchState({ questionInstancePerQuestionCode: new Map(map.entries()) });
                    action.modifiedInstanceCallback(updatedInstance);
                }),
            );
    }

    @Action(QuestionnaireCsrdAction.DeleteQuestionInstance)
    deleteInstance(ctx: StateContext<QuestionnaireCsrdModel>, action: QuestionnaireCsrdAction.DeleteQuestionInstance) {
        return this.questionnaireCsrdService
            .deleteQuestionInstance(action.organizationId, action.questionInstance)
            .pipe(
                tap(() => {
                    this.resultCsrdFetcherService.setInvalid();
                    const map = ctx.getState().questionInstancePerQuestionCode;
                    if (map === undefined) {
                        console.error('critital error');
                        return;
                    }
                    let instances = map.get(action.questionInstance.questionCode) ?? [];
                    instances = instances.filter((i) => i.id !== action.questionInstance.id);
                    map.set(action.questionInstance.questionCode, instances);
                    // Retrigger a patch state just in case
                    ctx.patchState({ questionInstancePerQuestionCode: new Map(map.entries()) });
                }),
            );
    }

    private mapInstancesWithQuestionCode(instances: QuestionCsrdInstanceDto[]): Map<string, QuestionCsrdInstanceDto[]> {
        return instances.reduce((acc, curInstance) => {
            if (!acc.has(curInstance.questionCode)) {
                acc.set(curInstance.questionCode, []);
            }
            const list = acc.get(curInstance.questionCode);
            if (list) {
                list.push(curInstance);
            }
            return acc;
        }, new Map<string, QuestionCsrdInstanceDto[]>());
    }

    @Selector([QuestionnaireCsrdState])
    public static getQuestionnaire(state: QuestionnaireCsrdModel) {
        return state.questionnaire;
    }

    @Selector([QuestionnaireCsrdState])
    public static getQuestionInstances(state: QuestionnaireCsrdModel) {
        return state.questionInstancePerQuestionCode;
    }
}
